import { GraphQLClient } from 'graphql-request'
import { doCaptchaGuardedRequest, convertConsumerToUserInfo } from './helpers'
import Hotsite from './graphql/queries/hotsite.query.gql'
import UserExists from './graphql/queries/userexists.query.gql'
import CreateConsumer from './graphql/queries/createConsumer.query.gql'
import Login from './graphql/queries/login.query.gql'
import TokenLogin from './graphql/queries/tokenLogin.query.gql'
import ChangePassword from './graphql/queries/changePassword.query.gql'
import UpdateConsumer from './graphql/queries/updateConsumer.query.gql'
import UpdateConsumerAddress from './graphql/queries/updateConsumerAddress.query.gql'
import PasswordRecover from './graphql/queries/passwordRecover.query.gql'
import PasswordRecoverSms from './graphql/queries/passwordRecoverSms.query.gql'
import ResetPassword from './graphql/queries/resetPassword.query.gql'
import ResetPasswordSms from './graphql/queries/resetPasswordSms.query.gql'
import TokenValidation from './graphql/queries/tokenValidation.query.gql'
import PhoneValidation from './graphql/queries/phoneValidation.query.gql'
import CreateDeleteDataRequest from './graphql/queries/createDeleteDataRequest.query.gql'
import ResendSignUpConfirmationConsumer from './graphql/queries/resendSignUpConfirmationByConsumer.query.gql'
import SignUpConfirmationByLink from './graphql/queries/signUpConfirmationByLink.query.gql'
import Moment from 'moment'

const url = process.env.VUE_APP_REGISTRY_URL
const apolloFederationUrl = process.env.VUE_APP_APOLLO_FEDERATION_URL

const client = new GraphQLClient(url)

// Helper function to commit hotsite data to store
export function commitHotsiteData(commit, hotsiteData) {
  const { hotsite } = hotsiteData

  document.title = hotsite.name ? `Clube ${hotsite.name}` : 'Clube'
  client.setHeader('accountid', hotsite.accountId)
  commit('ACCOUNT_ID', hotsite.accountId)
  commit('TERMS', hotsite.terms)
  commit('SHOW_DELETE_BUTTONS', hotsite.delete_data_requests)
  commit('SHOW_PARTIAL_DELETE_BUTTONS', hotsite.delete_partial_data_requests)
  commit('FIELDS', hotsite.configuration.fields)
  commit('ALLOWED_PERSON_TYPES_SIGNUP', hotsite.configuration.fields.allowed_person_types_signup)
  commit('ASSETS', hotsite.configuration.assets)
  commit('PIXEL', hotsite.configuration.facebook_pixel_id)
  commit('GOOGLE_ANALYTICS', hotsite.configuration.google_analytics_id)
  commit('ALLOWED_COUNTRIES', hotsite.configuration.allowed_countries)
  commit('LOGIN_MENU', hotsite.configuration.menu.login)
  commit('POSITION', hotsite.configuration.assets.positions.formImage || 'RIGHT')
  commit('CONTACTS', hotsite.contacts)
  commit('SMS_CHANNEL', hotsite.passwordRecoveryChannels.sms)
  commit('SOCIAL_MEDIA', hotsite.socialMedia)
  commit('ALLOWED_MODULES', hotsite.configuration.modules || {})
  commit('HOTSITE_LINKS', hotsite.hotsiteLinks)
  commit('APP_STORES', {
    enable_apps: hotsite.enable_apps,
    app_store_link: hotsite.appStoresLinks ? hotsite.appStoresLinks.app_store : '',
    play_store_link: hotsite.appStoresLinks ? hotsite.appStoresLinks.play_store : ''
  })
  commit('FINISH_SCREEN_WHATSAPP', {
    enable_whatsapp_link: hotsite.enable_whatsapp_link,
    whatsapp_link: hotsite.whatsapp_link
  })
  commit('CAPTCHA_KEY', hotsite.captcha_key)
  commit('SIGNUP_TAG', hotsite.configuration.signup_tag)
}

export const actions = {
  async getClient({ commit, state }, hostname) {
    commit('LOADING', true)
    return new Promise((resolve, reject) => {
      client
        .request(Hotsite, {
          url: hostname
        })
        .then(data => {
          commitHotsiteData(commit, data)
          resolve()
        })
        .catch(err => {
          reject(err)
        })
        .finally(() => commit('LOADING', false))
    })
  },

  async setHotsiteConfig({ commit }, config) {
    commitHotsiteData(commit, config)
  },

  async createConsumer({ commit, state }, value) {
    commit('LOADING', true)
    try {
      const data = await doCaptchaGuardedAction({ commit, state }, CreateConsumer, value)
      return data
    } catch (error) {
      console.warn(error)
      throw error
    } finally {
      commit('LOADING', false)
    }
  },

  async checkUniqueUser({ commit, state }, value) {
    commit('LOADING_ID', true)
    try {
      const data = await doCaptchaGuardedAction({ commit, state }, UserExists, value)
      commit('HIDDEN_EMAIL', data.checkConsumerHasLoginAndPassword.hashedConsumerEmail)
      commit('HIDDEN_PHONE', data.checkConsumerHasLoginAndPassword.hashedConsumerPhone)
      commit('DELETE_REQUEST', data.checkConsumerHasLoginAndPassword.hasPendingDeleteDataRequest)
      commit('SET_IS_CONFIRMED', data.checkConsumerHasLoginAndPassword.isConfirmed)
      return data.checkConsumerHasLoginAndPassword
    } catch (error) {
      console.warn(error)
      throw error
    } finally {
      commit('LOADING_ID', false)
    }
  },

  async login({ commit, state }, loginParams) {
    const { login } = await doCaptchaGuardedAction({ commit, state }, Login, loginParams)

    commit('ACCESS_TOKEN', login.accessToken)
    commit('USER_INFO', convertConsumerToUserInfo(login.consumer))
  },

  tokenLogin({ commit }, token) {
    return new Promise((resolve, reject) => {
      client
        .request(TokenLogin, token)
        .then(({ tokenLogin }) => {
          commit('ACCESS_TOKEN', tokenLogin.access_token)
          commit('USER_INFO', { ...tokenLogin.user, birth_date: Moment(tokenLogin.user.birth_date, 'YYYY-MM-DD').format('DD-MM-YYYY') })
          resolve()
        })
        .catch(error => {
          reject(error)
        })
    })
  },

  changePassword({ state }, params) {
    const apolloFederationClient = new GraphQLClient(apolloFederationUrl, {
      headers: {
        accountid: state.accountId,
        authorization: `Bearer ${state.access_token}`
      }
    })
    return apolloFederationClient.request(ChangePassword, params)
  },

  async updateConsumer({ commit, state }, params) {
    commit('LOADING', true)
    const apolloFederationClient = new GraphQLClient(apolloFederationUrl, {
      headers: {
        accountid: state.accountId,
        authorization: `Bearer ${state.access_token}`
      }
    })
    try {
      const { data, ...remainingParams } = params
      const { addresses, ...updateConsumerData } = data
      const responseUpdateConsumer = await apolloFederationClient.request(UpdateConsumer, {
        data: updateConsumerData,
        ...remainingParams
      })
      await apolloFederationClient.request(UpdateConsumerAddress, { data: addresses[0] })
      return responseUpdateConsumer.updateConsumer
    } catch (error) {
      console.warn(error)
      throw error
    } finally {
      commit('LOADING', false)
    }
  },

  recoverPassword({ commit }, params) {
    return new Promise((resolve, reject) => {
      client
        .request(PasswordRecover, params)
        .then(() => {
          resolve()
        })
        .catch(err => {
          reject(err)
        })
    })
  },

  recoverPasswordSms({ commit, state }, params) {
    return new Promise((resolve, reject) => {
      client.setHeader('accountid', state.accountId)
      client
        .request(PasswordRecoverSms, params)
        .then(data => {
          if (data.requestPhoneValidation.request_id) commit('REQUEST_ID', data.requestPhoneValidation.request_id)
          commit('COOLDOWN', data.requestPhoneValidation.cooldown)
          commit('MESSAGE_SENT', data.requestPhoneValidation.message_sent)
          resolve()
        })
        .catch(err => {
          reject(err)
        })
    })
  },

  resetPassword({ commit }, params) {
    return new Promise((resolve, reject) => {
      client
        .request(ResetPassword, params)
        .then(({ resetPassword }) => {
          commit('ACCESS_TOKEN', resetPassword.access_token)
          commit('USER_INFO', { ...resetPassword.user, birth_date: Moment(resetPassword.user.birth_date, 'YYYY-MM-DD').format('DD-MM-YYYY') })
          resolve(resetPassword.user.document_no)
        })
        .catch(err => {
          reject(err)
        })
    })
  },

  resetPasswordBySms({ commit, state }, params) {
    return new Promise((resolve, reject) => {
      client.setHeader('authorization', `Bearer ${state.access_token}`)
      client.setHeader('accountid', state.accountId)
      client
        .request(ResetPasswordSms, params)
        .then(() => {
          resolve()
        })
        .catch(err => {
          reject(err)
        })
    })
  },

  setIsApp({ commit }, params) {
    commit('SET_IS_APP', params)
  },

  tokenValidation({ commit }, params) {
    commit('LOADING', true)
    return new Promise((resolve, reject) => {
      client
        .request(TokenValidation, params)
        .then(() => {
          resolve()
        })
        .catch(err => {
          reject(err)
        })
        .finally(() => {
          setTimeout(() => {
            commit('LOADING', false)
          }, 500)
        })
    })
  },

  validatePin({ commit, state }, params) {
    return new Promise((resolve, reject) => {
      client.setHeader('accountid', state.accountId)
      client
        .request(PhoneValidation, { ...params, request_id: state.request_id })
        .then(data => {
          const login = data.phoneValidation
          if (login) {
            commit('IS_RECOVER_SMS', true)
            commit('ACCESS_TOKEN', login.access_token)
            commit('USER_INFO', { ...login.user, birth_date: Moment(login.user.birth_date, 'YYYY-MM-DD').format('DD-MM-YYYY') })
          }
          resolve()
        })
        .catch(err => {
          reject(err)
        })
    })
  },

  createDeleteDataRequest({ commit, state }, params) {
    const apolloFederationClient = new GraphQLClient(apolloFederationUrl, {
      headers: {
        accountid: state.accountId,
        authorization: `Bearer ${state.access_token}`
      }
    })
    return apolloFederationClient.request(CreateDeleteDataRequest, params)
  },

  async resendSignUpValidationByConsumer({ commit, state }, params) {
    try {
      const hasSend = await client.request(ResendSignUpConfirmationConsumer, params)
      return hasSend
    } catch (err) {
      throw err
    }
  },

  async signUpConfirmationByLink({ commit, state }, { code }) {
    const apolloFederationClient = new GraphQLClient(apolloFederationUrl, {
      headers: {
        accountid: state.accountId,
        'auth-shortcode': code
      }
    })

    try {
      const { signUpConfirmationByLink } = await apolloFederationClient.request(SignUpConfirmationByLink)
      return signUpConfirmationByLink
    } catch (err) {
      throw err
    }
  },

  setIsExternalAuth({ commit }, params) {
    commit('SET_IS_EXTERNAL_AUTH', params)
  }
}
export const doCaptchaGuardedAction = async ({ commit, state }, gql, variables, extraHeaders) => {
  if (state.allowed_modules.captcha) {
    const { data, token } = await doCaptchaGuardedRequest(gql, variables, state.captcha_key, state.accountId, state.currentCaptchaToken, extraHeaders)
    commit('SET_CAPTCHA_TOKEN', token)
    return data
  } else {
    const apolloFederationClient = new GraphQLClient(apolloFederationUrl, {
      headers: {
        accountid: state.accountId,
        ...extraHeaders
      }
    })
    return apolloFederationClient.request(gql, variables)
  }
}
